import { cn } from '@/lib/utils';
import type { MerchantStatus } from '@/lib/merchants';
import { MERCHANT_STATUS_INFO } from '@/lib/merchants';

interface StatusBadgeProps {
  status: MerchantStatus;
  /** 'sm' shows just the dot + label, 'md' adds the description tooltip area, 'dot' shows only the dot */
  size?: 'dot' | 'sm' | 'md';
  className?: string;
}

/** Coloured status indicator for a merchant's activity level. */
export function StatusBadge({ status, size = 'sm', className }: StatusBadgeProps) {
  const info = MERCHANT_STATUS_INFO[status];

  if (size === 'dot') {
    return (
      <span
        className={cn('relative flex shrink-0', className)}
        title={info.label}
        aria-label={info.label}
      >
        {status === 'active' ? (
          <>
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
              style={{ backgroundColor: info.dotColor }}
            />
            <span
              className="relative inline-flex h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: info.dotColor }}
            />
          </>
        ) : (
          <span
            className="inline-flex h-2.5 w-2.5 rounded-full border border-white/20"
            style={{ backgroundColor: info.dotColor }}
          />
        )}
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium border',
        status === 'active' && 'bg-green-50 border-green-200 text-green-700 dark:bg-green-950/40 dark:border-green-800 dark:text-green-400',
        status === 'merchant' && 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950/40 dark:border-yellow-800 dark:text-yellow-400',
        status === 'new' && 'bg-white border-gray-200 text-gray-500 dark:bg-gray-900/40 dark:border-gray-700 dark:text-gray-400',
        className,
      )}
      title={info.description}
    >
      {/* Coloured dot */}
      <span className="relative flex shrink-0">
        {status === 'active' ? (
          <>
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
              style={{ backgroundColor: info.dotColor }}
            />
            <span
              className="relative inline-flex h-2 w-2 rounded-full"
              style={{ backgroundColor: info.dotColor }}
            />
          </>
        ) : (
          <span
            className="inline-flex h-2 w-2 rounded-full"
            style={{ backgroundColor: info.dotColor }}
          />
        )}
      </span>

      {size === 'md' ? info.label : info.label}
    </span>
  );
}
